/**
 * Arbeitsblatt-Mode: Foto vom Blatt laden, Textfelder per Klick setzen.
 * Positionen werden in PROZENT der Bildgröße gespeichert -> beim Druck/Resize stabil.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Camera,
  Printer,
  Save,
  FolderOpen,
  Trash2,
  X,
  Move,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { worksheetsApi, assetUrl, ApiError } from '@/lib/api';
import { useValidActiveProfile } from '@/lib/use-valid-profile';
import { useDebounce } from '@/lib/use-debounce';
import { useSettings } from '@/lib/settings';
import { printWithTip } from '@/lib/print';
import { reportSaveError } from '@/lib/doc-autosave';
import { AuthGuard } from '@/lib/auth-guard';
import { PrintHeader } from '@/components/print-header';
import type { Worksheet, WorksheetField } from '@schreibhilfe/shared';

export const Route = createFileRoute('/arbeitsblatt')({
  component: () => (
    <AuthGuard>
      <WorksheetScreen />
    </AuthGuard>
  ),
});

function WorksheetScreen() {
  const { activeProfileId } = useValidActiveProfile();
  const settings = useSettings();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const [sheet, setSheet] = useState<Worksheet | null>(null);
  const [fields, setFields] = useState<WorksheetField[]>([]);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [showSidebar, setShowSidebar] = useState(false);

  const { data: sheets = [] } = useQuery({
    queryKey: ['worksheets', activeProfileId],
    queryFn: () => worksheetsApi.list(activeProfileId!),
    enabled: !!activeProfileId,
  });

  const uploadMut = useMutation({
    mutationFn: (file: File) =>
      worksheetsApi.create({
        profileId: activeProfileId!,
        name: `Arbeitsblatt ${(sheets.length || 0) + 1}`,
        image: file,
      }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['worksheets'] });
      setSheet(created);
      setFields([]);
      savedFieldsRef.current = '[]';
      toast.success('Arbeitsblatt geladen ✓');
    },
    onError: (err) => {
      // Server-Meldung zeigen (z.B. „Dieses Dateiformat geht nicht …“ oder „zu groß“)
      let msg = 'Bitte nochmal versuchen.';
      if (err instanceof ApiError) {
        if (err.status === 401) return reportSaveError(err);
        try {
          msg = (JSON.parse(err.message) as { error?: string }).error ?? msg;
        } catch {
          /* kein JSON */
        }
      }
      toast.error('Foto konnte nicht geladen werden', { description: msg });
    },
  });

  // Zuletzt am Server gespeicherter Stand der Felder (für Auto-Speichern)
  const savedFieldsRef = useRef<string>('[]');

  // Speichervorgänge nacheinander, damit ein älterer Stand nie einen neueren überholt
  const chainRef = useRef<Promise<unknown>>(Promise.resolve());
  const queueSave = (id: string, f: WorksheetField[]) => {
    const run = chainRef.current.then(() => worksheetsApi.update(id, { fields: f }));
    chainRef.current = run.catch(() => undefined);
    return run;
  };

  const saveMut = useMutation({
    mutationFn: (vars: { id: string; fields: WorksheetField[]; silent?: boolean }) =>
      queueSave(vars.id, vars.fields),
    onSuccess: (_d, vars) => {
      // Nur merken, wenn das gespeicherte Blatt noch das geöffnete ist
      if (latest.current.sheet?.id === vars.id) savedFieldsRef.current = JSON.stringify(vars.fields);
      qc.invalidateQueries({ queryKey: ['worksheets'] });
      if (!vars.silent) toast.success('Arbeitsblatt gespeichert ✓');
    },
    // Früher ohne Fehlermeldung — ein gescheitertes Speichern blieb unbemerkt.
    onError: (err) => reportSaveError(err),
  });

  // Auto-Speichern der Felder wie in Schreiben/Rechnen (QC 19.10.2026).
  // Früher gingen ausgefüllte Felder verloren, wenn man ohne „Speichern“ zurückging.
  const debouncedFields = useDebounce(fields, 1500);
  useEffect(() => {
    if (!settings.autoSave || !sheet) return;
    const json = JSON.stringify(debouncedFields);
    if (json === savedFieldsRef.current) return;
    saveMut.mutate({ id: sheet.id, fields: debouncedFields, silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFields]);

  // Beim Verlassen: Ungespeichertes nachschieben
  const latest = useRef({ sheet, fields, autoSave: settings.autoSave });
  latest.current = { sheet, fields, autoSave: settings.autoSave };
  useEffect(() => {
    return () => {
      const { sheet: sh, fields: f, autoSave } = latest.current;
      if (autoSave && sh && JSON.stringify(f) !== savedFieldsRef.current) {
        void queueSave(sh.id, f)
          .then(() => qc.invalidateQueries({ queryKey: ['worksheets'] }))
          .catch(() => undefined);
      }
    };
  }, []);

  const delMut = useMutation({
    mutationFn: (id: string) => worksheetsApi.remove(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['worksheets'] });
      // War das gerade geöffnete Blatt dabei → Arbeitsfläche leeren
      if (sheet?.id === id) {
        setSheet(null);
        setFields([]);
        savedFieldsRef.current = '[]';
      }
      toast.success('Gelöscht');
    },
    onError: () => toast.error('Konnte nicht gelöscht werden'),
  });

  function pickFile() {
    fileRef.current?.click();
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file && activeProfileId) {
      flushCurrent();
      uploadMut.mutate(file);
    }
    e.target.value = '';
  }

  /** Ungespeicherte Felder des aktuellen Blatts sofort sichern (vor Blattwechsel). */
  function flushCurrent() {
    if (settings.autoSave && sheet && JSON.stringify(fields) !== savedFieldsRef.current) {
      saveMut.mutate({ id: sheet.id, fields, silent: true });
    }
  }

  function loadSheet(s: Worksheet) {
    flushCurrent();
    setSheet(s);
    setFields(s.fields);
    savedFieldsRef.current = JSON.stringify(s.fields);
    setShowSidebar(false);
  }

  function onImageClick(e: React.MouseEvent<HTMLDivElement>) {
    if (!hostRef.current) return;
    if ((e.target as HTMLElement).closest('.ws-field')) return;
    const rect = hostRef.current.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width) * 100;
    const yPct = ((e.clientY - rect.top) / rect.height) * 100;
    const newField: WorksheetField = { xPct, yPct, text: '' };
    setActiveIdx(fields.length);
    setFields((prev) => [...prev, newField]);
  }

  function updateField(idx: number, patch: Partial<WorksheetField>) {
    setFields((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  }

  function deleteField(idx: number) {
    setFields((prev) => prev.filter((_, i) => i !== idx));
    setActiveIdx(null);
  }

  // Verschieben mit Maus, Finger und Apple Pencil (Pointer Events).
  // Früher nur mit der Maus — am iPad ließen sich Felder nicht verschieben.
  function startDrag(idx: number, e: React.PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    const host = hostRef.current;
    if (!host) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startField = fields[idx]!;

    function onMove(mv: PointerEvent) {
      const rect = host!.getBoundingClientRect();
      const dxPct = ((mv.clientX - startX) / rect.width) * 100;
      const dyPct = ((mv.clientY - startY) / rect.height) * 100;
      updateField(idx, {
        xPct: Math.max(0, Math.min(95, startField.xPct + dxPct)),
        yPct: Math.max(0, Math.min(95, startField.yPct + dyPct)),
      });
    }
    function onUp() {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  if (!activeProfileId) {
    return (
      <div className="flex h-screen items-center justify-center p-6">
        <div className="max-w-md rounded-2xl bg-white p-8 text-center shadow-sm">
          <p className="text-lg font-bold text-gray-700">Kein Profil ausgewählt</p>
          <Button asChild className="mt-4">
            <Link to="/lehrer">Zum Lehrer-Bereich</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-sky-bottom">
      <header className="flex h-16 items-center gap-2 border-b-2 border-success/20 bg-white px-3">
        <Button asChild variant="ghost" size="icon" aria-label="Zurück">
          <Link to="/">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-success text-xl">
          📄
        </div>
        <div className="flex-1">
          <h1 className="text-base font-bold text-success">Arbeitsblatt</h1>
          <p className="text-xs text-gray-500">
            {sheet ? sheet.name : 'Lade ein Foto oder wähle ein Blatt'}
          </p>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onFileChange}
        />
        <Button variant="success" size="sm" onClick={pickFile}>
          <Camera className="h-4 w-4" /> Foto laden
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => sheet && saveMut.mutate({ id: sheet.id, fields })}
          disabled={!sheet || saveMut.isPending}
        >
          <Save className="h-4 w-4" /> Speichern
        </Button>
        <Button variant="success" size="sm" onClick={() => setShowSidebar(true)}>
          <FolderOpen className="h-4 w-4" /> Meine Blätter
        </Button>
        <Button variant="primary" size="sm" onClick={printWithTip}>
          <Printer className="h-4 w-4" /> Drucken
        </Button>
      </header>

      <main className="flex-1 overflow-auto p-4">
        <PrintHeader section="Arbeitsblatt" />
        {!sheet ? (
          <div className="flex h-full items-center justify-center">
            <div className="max-w-md rounded-2xl bg-white p-12 text-center shadow-sm">
              <Camera className="mx-auto mb-3 h-16 w-16 text-success" />
              <h2 className="text-lg font-bold text-gray-700">Noch kein Arbeitsblatt</h2>
              <p className="mt-2 text-sm text-gray-500">
                Tippe oben auf <strong>"Foto laden"</strong> oder <strong>"Meine Blätter"</strong>.
              </p>
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-5xl">
            <div
              ref={hostRef}
              onClick={onImageClick}
              className="ws-print-host relative inline-block w-full cursor-crosshair bg-white shadow-sm"
            >
              <img
                src={assetUrl(sheet.imageUrl)}
                alt={sheet.name}
                className="block w-full h-auto"
                draggable={false}
              />
              {fields.map((f, idx) => (
                <FieldOverlay
                  key={idx}
                  field={f}
                  active={activeIdx === idx}
                  onActivate={() => setActiveIdx(idx)}
                  onChange={(patch) => updateField(idx, patch)}
                  onDelete={() => deleteField(idx)}
                  onDragStart={(e) => startDrag(idx, e)}
                />
              ))}
            </div>
          </div>
        )}
      </main>

      <Dialog open={showSidebar} onOpenChange={setShowSidebar}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>📁 Meine Blätter</DialogTitle>
            <DialogDescription>
              Wähle ein gespeichertes Arbeitsblatt aus oder lösche es.
            </DialogDescription>
          </DialogHeader>
          {sheets.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500">
              Noch keine gespeicherten Blätter.
            </p>
          ) : (
            <div className="max-h-[60vh] space-y-2 overflow-y-auto">
              {sheets.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center gap-3 rounded-xl bg-gray-50 p-2 hover:bg-gray-100"
                >
                  <img
                    src={assetUrl(s.imageUrl)}
                    alt={s.name}
                    className="h-16 w-24 rounded-md object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-bold text-gray-800">{s.name}</div>
                    <div className="text-xs text-gray-500">
                      {new Date(s.updatedAt).toLocaleString('de-AT', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                      {' · '}
                      {s.fields.length} Feld{s.fields.length === 1 ? '' : 'er'}
                    </div>
                  </div>
                  <Button variant="primary" size="sm" onClick={() => loadSheet(s)}>
                    Öffnen
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Löschen"
                    onClick={() => {
                      if (confirm(`„${s.name}“ wirklich löschen? Das Foto und alle Felder werden entfernt.`)) {
                        delMut.mutate(s.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-danger" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldOverlay({
  field,
  active,
  onActivate,
  onChange,
  onDelete,
  onDragStart,
}: {
  field: WorksheetField;
  active: boolean;
  onActivate: () => void;
  onChange: (patch: Partial<WorksheetField>) => void;
  onDelete: () => void;
  onDragStart: (e: React.PointerEvent) => void;
}) {
  return (
    <div
      className="ws-field absolute"
      style={{
        left: `${field.xPct}%`,
        top: `${field.yPct}%`,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onActivate();
      }}
    >
      {active && (
        <>
          <button
            type="button"
            className="absolute -left-4 -top-4 z-10 flex h-9 w-9 cursor-move touch-none items-center justify-center rounded-full border-2 border-white bg-success text-white shadow-md print:hidden"
            onPointerDown={onDragStart}
            title="Verschieben"
          >
            <Move className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className="absolute -right-4 -top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-danger text-white shadow-md print:hidden"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            title="Löschen"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </>
      )}
      <textarea
        value={field.text}
        onChange={(e) => onChange({ text: e.target.value })}
        onFocus={onActivate}
        // Neues Feld: sofort lostippen können, ohne nochmal hineinzutippen
        autoFocus={active && field.text === ''}
        placeholder="Hier tippen…"
        spellCheck={false}
        autoCapitalize="sentences"
        rows={1}
        className="ws-field-editor block min-w-[180px] resize rounded-lg border-[3px] border-danger bg-yellow-100 px-3 py-2 text-2xl font-bold leading-tight text-gray-900 shadow-md outline-none transition-all"
        style={{ minHeight: 50 }}
      />
    </div>
  );
}
